import Link from 'next/link';
import Tick from 'components/Svg/Tick';
import Shield from 'components/Svg/Shield';
import { formatDate } from 'utils/newsroom';
import { FCA_LABEL, checkedLine, hasFca } from 'utils/fca';
import { itemHref, profilePath, roleAndFirm } from 'utils/directory';
import ShowMoreList from './ShowMoreList';
import styles from 'styles/modules/BrokerDirectory.module.scss';

const coverageLabel = (count) =>
    `${Number(count || 0).toLocaleString('en-GB')} ${
        Number(count) === 1 ? 'piece' : 'pieces'
    } of media coverage`;

// With "Most recent" above them, three pieces show before "Show more".
const OPEN_MORE_ITEMS = 2;

const sameItem = (a, b) =>
    !!a &&
    !!b &&
    a.date === b.date &&
    a.headline === b.headline &&
    a.outlet === b.outlet;

// Outlet, headline as the link, date: the light line every listed piece gets.
const ItemLine = ({ item }) => {
    const href = itemHref(item);
    const text = item.headline || item.outlet || 'Read the coverage';
    return (
        <>
            <span className={styles.outlet}>{item.outlet}</span>
            {(href || item.headline) && ': '}
            {!href ? (
                item.headline
            ) : item.kind === 'story' ? (
                <Link href={href}>
                    <a>{text}</a>
                </Link>
            ) : (
                <a href={href} target="_blank" rel="noopener noreferrer">
                    {text}
                </a>
            )}{' '}
            <time className={styles.date} dateTime={item.date}>
                {formatDate(item.date)}
            </time>
        </>
    );
};

export default function BrokerCard({ broker }) {
    const role = roleAndFirm(broker);
    const item = broker.latestItem;
    const isStory = item?.kind === 'story';
    const profile = profilePath(broker.slug);
    const more = (broker.items || []).filter((other) => !sameItem(other, item));

    return (
        <li className={styles.card}>
            <div className={styles.cardHead}>
                <h2 className={styles.name}>
                    {profile ? (
                        <Link href={profile}>
                            <a>{broker.name}</a>
                        </Link>
                    ) : (
                        broker.name
                    )}
                </h2>
                {broker.verified && (
                    <span className={styles.verified}>
                        <span aria-hidden="true">
                            <Tick fill="currentColor" />
                        </span>
                        Verified
                    </span>
                )}
                {hasFca(broker.fca) && (
                    <span
                        className={`${styles.verified} ${styles.fca}`}
                        title={checkedLine(broker.fca)}
                    >
                        <span aria-hidden="true">
                            <Shield fill="currentColor" />
                        </span>
                        {FCA_LABEL}
                    </span>
                )}
            </div>
            {(role || broker.town) && (
                <p className={styles.meta}>
                    {[role, broker.town].filter(Boolean).join(' · ')}
                </p>
            )}
            {broker.coverageCount > 0 && (
                <p className={styles.count}>
                    {coverageLabel(broker.coverageCount)}
                </p>
            )}
            {item && (
                <p className={styles.item}>
                    <span className={styles.itemLabel}>
                        {isStory ? 'Quoted on' : 'Most recent'}
                    </span>{' '}
                    <ItemLine item={item} />
                </p>
            )}
            {more.length > 0 && (
                <ShowMoreList
                    id={`coverage-${broker.slug}`}
                    items={more}
                    open={OPEN_MORE_ITEMS}
                    itemKey={(other, index) =>
                        `${other.kind}:${other.url || other.alertSlug}:${index}`
                    }
                    nouns={['piece', 'pieces']}
                    listClassName={styles.moreItems}
                    hiddenClassName={styles.folded}
                    buttonClassName={styles.showMore}
                    renderItem={(other) => <ItemLine item={other} />}
                />
            )}
        </li>
    );
}
